import {
  Component,
  computed,
  inject,
  Injector,
  NgZone,
  OnDestroy,
  OnInit,
  Type,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { animationFrameScheduler, fromEvent, map, Subject, takeUntil, throttleTime } from 'rxjs';
import { WalletService } from '../services/wallet.service';
import { Router } from '@angular/router';
import { NgClass, NgComponentOutlet } from '@angular/common';
import {
  DEFAULT_WINDOW_ANIMATION,
  WINDOW_ANIMATION,
  WindowAnimationConfig,
  WindowAnimState,
} from '../../shared/windows/window.tokens';
import { DESKTOP_APPS } from '../../constants/home.constants';
import { DesktopApp } from '../../shared/interfaces/desktop-app.interface';
import { MusicService } from '../services/music.service';

@Component({
  selector: 'app-root',
  imports: [FormsModule, NgComponentOutlet, NgClass],
  templateUrl: './home.component.html',
  styleUrl: './home.component.scss',
})
export class HomeComponent implements OnInit, OnDestroy {
  private readonly walletService = inject(WalletService);
  private readonly router = inject(Router);
  private readonly ngZone = inject(NgZone);
  private readonly musicService = inject(MusicService);

  protected readonly walletConnection = computed(() => this.walletService.walletConnection());

  async disconnectWallet(): Promise<void> {
    this.walletService.disconnectWallet();
    await this.router.navigate(['/login']);
  }

  selectedIcon: string | null = null;
  contextMenuVisible = false;
  startMenuVisible = false;
  currentTime = '';
  private timeInterval?: number;

  private destroy$ = new Subject<void>();
  private readonly injector = inject(Injector);
  private nextWindowId = 1;

  desktopApps: DesktopApp[] = DESKTOP_APPS;
  private lastTapByIcon = new Map<string, number>();

  onIconClick(ev: MouseEvent, app: DesktopApp) {
    const isCoarse = window.matchMedia?.('(pointer: coarse)').matches || navigator.maxTouchPoints > 0;
    if (isCoarse) {
      ev.preventDefault();
      const now = performance.now();
      const last = this.lastTapByIcon.get(app.id) || 0;
      if (now - last < 350) {
        this.openApp(app);
        this.lastTapByIcon.delete(app.id);
      } else {
        this.selectedIcon = app.id;
        this.lastTapByIcon.set(app.id, now);
      }
    } else {
      this.selectedIcon = app.id;
    }
  }

  genericWindows: Array<{
    id: number;
    appId?: string;
    title: string;
    component: Type<unknown>;
    injector?: Injector;
    animation?: WindowAnimationConfig;
    animState?: WindowAnimState;
    position: { x: number; y: number };
    size: { width: number; height: number };
    minimized: boolean;
    zIndex: number;
    visible: boolean;
  }> = [];

  openApp(app: DesktopApp) {
    if (app.action.kind === 'navigate') {
      void this.router.navigate([app.action.route]);
      return;
    }
    const id = this.nextWindowId++;
    const animation = app.action.animation ?? DEFAULT_WINDOW_ANIMATION;
    const injector = Injector.create({
      providers: [{ provide: WINDOW_ANIMATION, useValue: animation }],
      parent: this.injector,
    });
    const w = {
      id,
      appId: app.id,
      title: app.title,
      component: app.action.component,
      injector,
      animation,
      animState: 'opening' as WindowAnimState,
      position: app.action.position,
      size: app.action.size,
      minimized: false,
      zIndex: Math.max(...this.genericWindows.map((x) => x.zIndex), 200) + 1,
      visible: true,
    };
    this.genericWindows.push(w);
    // Start background music for specific apps
    if (app.id === 'gang') {
      this.musicService.startBackgroundMusic('assets/audio/slow_Boss_Time.mp3');
    } else if (app.id === 'keygen') {
      this.musicService.startBackgroundMusic('assets/audio/unreal_superhero_3.mp3');
    }
    setTimeout(() => {
      const found = this.genericWindows.find((x) => x.id === id);
      if (found && found.animState === 'opening') {
        // Keep the final animated size (generic): snapshot current DOM size
        try {
          const el = document.querySelector(`[data-win-id="${id}"]`) as HTMLElement | null;
          if (el) {
            const rect = el.getBoundingClientRect();
            const width = Math.round(rect.width);
            const height = Math.round(rect.height);
            if (width > 0 && height > 0) found.size = { width, height };
          }
        } catch {
          /* no-op */
        }
        found.animState = null;
      }
    }, animation.openDurationMs);
    this.selectedIcon = null;
  }

  closeGenericWindow(id: number) {
    const w = this.genericWindows.find((x) => x.id === id);
    if (!w) return;
    if (w.animation) {
      w.animState = 'closing';
      setTimeout(() => {
        if (w.appId === 'gang' || w.appId === 'keygen') {
          this.musicService.stopBackgroundMusic();
        }
        this.genericWindows = this.genericWindows.filter((x) => x.id !== id);
      }, w.animation.closeDurationMs);
    } else {
      if (w.appId === 'gang' || w.appId === 'keygen') {
        this.musicService.stopBackgroundMusic();
      }
      this.genericWindows = this.genericWindows.filter((x) => x.id !== id);
    }
  }

  minimizeGenericWindow(id: number) {
    const w = this.genericWindows.find((w) => w.id === id);
    if (!w) return;
    if (w.animation) {
      w.animState = 'minimizing';
      setTimeout(() => {
        w.minimized = true;
        w.animState = null;
      }, w.animation.minimizeDurationMs);
    } else {
      w.minimized = true;
    }
  }

  restoreGenericWindow(id: number) {
    const w = this.genericWindows.find((w) => w.id === id);
    if (w) {
      w.minimized = false;
      w.visible = true;
      w.zIndex = Math.max(...this.genericWindows.map((x) => x.zIndex), 200) + 1;
      if (w.animation) {
        w.animState = 'restoring';
        setTimeout(() => (w.animState = null), w.animation.restoreDurationMs);
      }
    }
  }

  bringGenericToFront(id: number) {
    const maxZ = Math.max(...this.genericWindows.map((x) => x.zIndex), 200);
    const w = this.genericWindows.find((w) => w.id === id);
    if (w) w.zIndex = maxZ + 1;
  }

  animClassFor(w: {
    animation?: WindowAnimationConfig;
    animState?: WindowAnimState | null;
  }): string | undefined {
    if (!w.animState) return undefined;
    const anim = w.animation ?? DEFAULT_WINDOW_ANIMATION;
    switch (w.animState) {
      case 'opening':
        return anim.openClass;
      case 'closing':
        return anim.closeClass;
      case 'minimizing':
        return anim.minimizeClass;
      case 'restoring':
        return anim.restoreClass;
      default:
        return undefined;
    }
  }

  startGenericDrag(event: MouseEvent, id: number) {
    event.preventDefault();
    const w = this.genericWindows.find((x) => x.id === id);
    if (!w) return;
    const startX = event.clientX;
    const startY = event.clientY;
    const initialX = w.position.x;
    const initialY = w.position.y;
    this.bringGenericToFront(id);
    this.ngZone.runOutsideAngular(() => {
      const move$ = fromEvent<MouseEvent>(document, 'mousemove').pipe(
        throttleTime(0, animationFrameScheduler),
        map((e) => ({ x: initialX + (e.clientX - startX), y: initialY + (e.clientY - startY) }))
      );
      const up$ = fromEvent(document, 'mouseup');
      move$.pipe(takeUntil(up$), takeUntil(this.destroy$)).subscribe((pos) => {
        w.position = pos;
        this.ngZone.run(() => {
          /* empty */
        });
      });
    });
  }

  startGenericResize(event: MouseEvent, id: number) {
    event.preventDefault();
    event.stopPropagation();
    const w = this.genericWindows.find((x) => x.id === id);
    if (!w) return;
    const startX = event.clientX;
    const startY = event.clientY;
    const initialW = w.size.width;
    const initialH = w.size.height;
    this.bringGenericToFront(id);
    this.ngZone.runOutsideAngular(() => {
      const move$ = fromEvent<MouseEvent>(document, 'mousemove').pipe(
        throttleTime(0, animationFrameScheduler),
        map((e) => ({
          width: Math.max(320, initialW + (e.clientX - startX)),
          height: Math.max(220, initialH + (e.clientY - startY)),
        }))
      );
      const up$ = fromEvent(document, 'mouseup');
      move$.pipe(takeUntil(up$), takeUntil(this.destroy$)).subscribe((size) => {
        w.size = size;
        this.ngZone.run(() => {
          /* empty */
        });
      });
    });
  }

  async ngOnInit() {
    try {
      await this.walletService.restoreWalletConnection();
      if (this.walletConnection()?.actor) {
        await this.router.navigate(['/home']);
      }
    } catch (error) {
      console.error('Wallet connection error:', error);
      await this.router.navigate(['/login']);
    }
    this.updateTime();
    this.timeInterval = window.setInterval(() => this.updateTime(), 1000);
    this.setupGlobalEventListeners();
  }

  ngOnDestroy() {
    if (this.timeInterval) {
      clearInterval(this.timeInterval);
    }
    this.destroy$.next();
    this.destroy$.complete();
  }

  private setupGlobalEventListeners() {
    fromEvent(document, 'click')
      .pipe(takeUntil(this.destroy$))
      .subscribe((event: Event) => {
        const target = event.target as HTMLElement;

        if (
          this.startMenuVisible &&
          !target.closest('.start-button') &&
          !target.closest('.start-menu')
        ) {
          this.startMenuVisible = false;
        }

        if (target.classList.contains('desktop')) {
          this.selectedIcon = null;
        }
      });
  }

  updateTime() {
    const now = new Date();
    this.currentTime = now.toLocaleTimeString('de-DE', {
      hour: '2-digit',
      minute: '2-digit',
    });
  }

  selectIcon(icon: string) {
    this.selectedIcon = icon;
  }

  toggleStartMenu() {
    this.startMenuVisible = !this.startMenuVisible;
    if (this.startMenuVisible) {
      this.contextMenuVisible = false;
    }
  }
}
